/**
 * Docuswift Portal - Main Application Entry Point
 * Refactored with class-based architecture for better maintainability
 */

// Core imports

import { eventBus } from './core/EventBus.js';
// Component imports
import { TableManager } from './components/TableManager.js';
import { Pagination } from './components/Pagination.js';
import { LoginForm } from './components/LoginForm.js';
import { FormRepeater } from './components/FormRepeater.js';
import { DatePicker } from './components/DatePicker.js';
import { FileUpload } from './components/FileUpload.js';
import { MobileMenu } from './components/MobileMenu.js';
import { RequestDetails } from './components/RequestDetails.js';
import { DetailsRow } from './components/DetailsRow.js';
import { Tabs } from './components/Tabs.js';
import { Chat } from './chat/chat.js';
import { CaseManager } from './pages/case-manager.js';
import { DocxPreview } from './components/DocxPreview.js';
import { FilePdfMerge } from './components/FilePdfMerge.js';
import { Notification } from './notifications/notifications.js';
import { ChoicesSelect } from './components/ChoicesSelect.js';
import { NiceSelectComponent } from './components/NiceSelect.js';
// import { Tabs } from './components/Tabs.js';
import { Facility } from './pages/facility.js';
import { RequestsFiltersDrawer } from './components/RequestsFiltersDrawer.js';
import { initRoleTours } from './tours/index.js';


// Utility imports
import { ChatScroller } from './utils/ChatScroller.js';
import { InactivityManager } from './utils/InactivityManager.js';
import { openRequestDetailFromLink } from './utils/openRequestLink.js';
/**
 * Application class - manages all components
 */
class DocuswiftPortalApp {
    constructor() {
        this.components = {};
    }

    /**
     * Initialize all application components
     */
    init() {
        console.log('Initializing Docuswift Portal...');
        window.DocuswiftRequestsFiltersDrawer = RequestsFiltersDrawer;

        // Initialize core components
        this.initTableComponents();
        this.initAuthComponents();
        this.initSessionTimeout();
        this.initFormComponents();
        this.initUIComponents();
        this.initUtilities();
        this.initChatComponents();
        this.initChoicesComponents();
        this.initResetPasswordValidation();
        this.initForgotPasswordForm();
        this.initSupportModal();

        this.loadPageSpecificScripts();

        this.initNotificationComponents();
        this.initRequestQuickLinks();
        initRoleTours();

        console.log('Docuswift Portal initialized successfully');
    }

    initChatComponents() {
        const chat = new Chat();
        chat.init();
        this.components.chat = chat;
    }

    initChoicesComponents() {
        const tomSelect = new ChoicesSelect('.choices-select', {
            create: false,
            sortField: {
                field: "text",
                direction: "asc"
            }
        });

        tomSelect.init();
        this.components.tomSelect = tomSelect;
    }

    initNotificationComponents() {
        const notification = new Notification();
        notification.init();
        this.components.notification = notification;

        // Shortcuts elsewhere on the page (dashboard stat cards, side-panel
        // "View all" links) that should open the bell's dropdown for a given
        // category instead of duplicating the notifications UI.
        document.querySelectorAll('.notif-jump[data-notif-category]').forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                notification.openCategoryByName(el.dataset.notifCategory);
            });
        });
    }

    /**
     * Plain links elsewhere on the page that point at a request (e.g. the
     * dashboard's "Message Alerts"/"Recent Activity" previews) — open them
     * in place via RequestDetails instead of a full page navigation, same as
     * the notification bell's dropdown items already do.
     */
    initRequestQuickLinks() {
        document.addEventListener('click', (e) => {
            const link = e.target.closest('.js-request-link');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href) return;

            e.preventDefault();
            if (!openRequestDetailFromLink(href)) {
                window.location.href = href;
            }
        });
    }

    initSessionTimeout() {
        console.log('Initializing session timeout manager...');

        const inactivityManager = new InactivityManager();

        inactivityManager.init();
        this.components.inactivityManager = inactivityManager;
    }

    /**
     * Initialize table-related components
     */
    initTableComponents() {
        // Main table manager
        const mainTable = new TableManager('.main_table', {
            perPage: 10,
            apiAction: 'docuswift_portal_main_table',
            eventNamespace: 'main'
        });
        
        if (mainTable.exists()) {
            mainTable.init();
            this.components.mainTable = mainTable;
        }

        // Requests dashboard: TableManager + filters + pagination live in requests-dashboard.js (enqueued on that template only).

        // Main table pagination
        const pagination = new Pagination('.docuswift_portal_pagination:not(.requests-pagination)', {
            perPage: 10,
            range: 1,
            eventNamespace: 'main'
        });
        
        if (pagination.exists()) {
            pagination.init();
            this.components.pagination = pagination;
        }

        // Requests pagination: see requests-dashboard.js

        // Facilities table manager
        const facilitiesTable = new TableManager('.facilities_table', {
            perPage: 10,
            apiAction: 'docuswift_portal_facilities_table',
            searchInputSelector: 'input.search.facilities-search',
            tableBodySelector: '.facilities_table tbody',
            sortableHeadersSelector: '.facilities_table .sortable',
            eventNamespace: 'facilities'
        });

        if (facilitiesTable.exists()) {
            facilitiesTable.init();
            this.components.facilitiesTable = facilitiesTable;
        }

        // Facilities pagination
        const facilitiesPagination = new Pagination('.docuswift_portal_pagination.facilities-pagination', {
            perPage: 10,
            range: 1,
            eventNamespace: 'facilities'
        });

        if (facilitiesPagination.exists()) {
            facilitiesPagination.init();
            this.components.facilitiesPagination = facilitiesPagination;
        }

        // Details row toggle
        this.components.detailsRow = new DetailsRow();
    }

    /**
     * Initialize authentication components
     */
    initAuthComponents() {
        const loginForm = new LoginForm('#custom-login-form', {
            apiAction: 'custom_ajax_login'
        });

        if (loginForm.exists()) {
            loginForm.init();
            this.components.loginForm = loginForm;
        }
    }

    /**
     * Initialize form components
     */
    initFormComponents() {
        // Date picker
        const datePicker = new DatePicker({
            inputSelector: '.date-picker',
            format: 'MM/DD/YYYY'
        });
        datePicker.init();
        this.components.datePicker = datePicker;

        // Form repeater
        const formRepeater = new FormRepeater('#add-request-form', {
            repeaterSelector: '.form-section[data-repeater-id]',
            addButtonSelector: '.add-repeater-item',
            removeButtonSelector: '.remove-repeater-item'
        });

        if (formRepeater.exists()) {
            formRepeater.init();
            this.components.formRepeater = formRepeater;
        }

        // File upload containers
        const fileUploads = FileUpload.initAll('.file-upload-container');
        this.components.fileUploads = fileUploads;

        // Choices select
        // const choicesSelect = new ChoicesSelect('.choices-select', {
        //     removeItemButton: true,
        //     searchEnabled: true
        // });

        // if (choicesSelect.exists()) {
        //     choicesSelect.init();
        //     this.components.choicesSelect = choicesSelect;
        // }

        const tabs = new Tabs('[data-tabs]');
        // if (tabs.exists()) {
            tabs.init();
            this.components.tabs = tabs;
        // }
    }

    /**
     * Initialize UI components
     */
    initUIComponents() {
        // NiceSelect custom dropdowns
        new NiceSelectComponent().init();

        // Mobile menu
        const mobileMenu = new MobileMenu({
            menuSelector: 'div#header',
            overlaySelector: '.mobile_menu_overlay',
            toggleSelector: '.mobile_menu_btn'
        });
        mobileMenu.init();
        this.components.mobileMenu = mobileMenu;

        // Request details modal (regular users)
        const requestDetails = new RequestDetails({
            overlaySelector: '.request_detail_overlay',
            detailsSelector: '#request_detail',
            closeButtonSelector: '.close_request_detail',
            triggerSelector: 'table.requests_table tbody tr'
        });
        requestDetails.init();
        this.components.requestDetails = requestDetails;

        // Request details panel (case manager)
        if (document.getElementById('cm_request_detail')) {
            const cmRequestDetails = new RequestDetails({
                overlaySelector: '.cm-request-detail-overlay',
                detailsSelector: '#cm_request_detail',
                closeButtonSelector: '.close_request_detail',
                triggerSelector: '.case-manager-view-request',
                fetchAction: 'get_cm_request_detail',
                skeletonMarkupKey: 'cmDetailSkeletonMarkup',
            });
            cmRequestDetails.init();
            this.components.cmRequestDetails = cmRequestDetails;
        }

        // Request details panel (company admin)
        if (document.getElementById('company_admin_request_detail')) {
            const companyAdminRequestDetails = new RequestDetails({
                overlaySelector: '.company-admin-request-detail-overlay',
                detailsSelector: '#company_admin_request_detail',
                closeButtonSelector: '.close_request_detail',
                triggerSelector: '.company-admin-view-request',
                fetchAction: 'get_cm_request_detail',
                skeletonMarkupKey: 'companyAdminDetailSkeletonMarkup',
            });
            companyAdminRequestDetails.init();
            this.components.companyAdminRequestDetails = companyAdminRequestDetails;
        }

        const tabs = new Tabs('[data-tabs]');
        if (tabs.exists()) {
            tabs.init();
            this.components.tabs = tabs;
        }

        // DOCX/file preview modal
        const docxPreview = new DocxPreview();
        docxPreview.init();
        this.components.docxPreview = docxPreview;

        // File PDF merge (checkboxes + Generate PDF button)
        const filePdfMerge = new FilePdfMerge();
        filePdfMerge.init();
        this.components.filePdfMerge = filePdfMerge;

    }

    /**
     * Initialize Reset Password validation
     */
    initResetPasswordValidation() {
        const form  = document.getElementById('custom-reset-form');
        if (!form) return;

        

        const pass1 = document.getElementById('pass1');
        const pass2 = document.getElementById('pass2');
        const msg   = document.getElementById('login-message');

        const username =
            document.querySelector('input[name="rp_login"]')?.value || '';

        const email =
            document.querySelector('input[name="user_email"]')?.value || '';

        form.addEventListener('submit', (e) => {
            const password = pass1.value;
            const errors = [];
           
            msg.textContent = '';

            // Length, Lowercase, Uppercase, Number, Special Character
            if (password.length < 12 || !/[A-Z]/.test(password) || !/[0-9]/.test(password) || !/[\W_]/.test(password)){

                errors.push('<i>Password must contain an uppercase letter, a number, and a special character.</i>');
            }

            // Username check
            if (
                username &&
                password.toLowerCase().includes(username.toLowerCase())
            ) {
                errors.push('<i>Password must not contain your username.</i>');
            }

            // Confirm match
            if (password !== pass2.value) {
                errors.push('<i>Passwords do not match.</i>');
            }

            if (errors.length > 0) {
                e.preventDefault();

                msg.innerHTML = errors.join('<br>');
                msg.style.display = 'block';

                 
            }
        });
    }

    initSupportModal() {
        const modal = document.getElementById('support_modal');
        if (!modal) return;

        const form      = document.getElementById('support-request-form');
        const notice    = document.getElementById('support-form-notice');
        const submitBtn = form?.querySelector('button[type="submit"]');
        const overlay   = modal.querySelector('.overlay');

        const open = () => {
            form?.reset();
            notice.style.display = 'none';
            notice.className = '';
            modal.style.display = 'block';
            modal.style.opacity = 0;
            modal.style.transition = 'opacity 0.2s';
            requestAnimationFrame(() => { modal.style.opacity = 1; });
        };

        const close = () => {
            modal.style.opacity = 0;
            setTimeout(() => { modal.style.display = 'none'; }, 200);
        };

        const showNotice = (text, type) => {
            notice.textContent = text;
            notice.className   = 'support-form-notice support-form-notice--' + type;
            notice.style.display = 'block';
        };

        // Open triggers — button with id="#support-request"
        document.addEventListener('click', (e) => {
            if (e.target.closest('[id="#support-request"]')) {
                e.preventDefault();
                open();
            }
        });

        modal.querySelector('.close_modal')?.addEventListener('click', (e) => { e.preventDefault(); close(); });
        modal.querySelector('.cancel_button')?.addEventListener('click', () => close());
        overlay?.addEventListener('click', () => close());

        form?.addEventListener('submit', async (e) => {
            e.preventDefault();

            submitBtn.disabled = true;
            submitBtn._orig    = submitBtn.textContent;
            submitBtn.innerHTML = '<span class="btn-spinner"></span>';
            notice.style.display = 'none';

            try {
                const params = new URLSearchParams({
                    action:  'docuswift_support_request',
                    nonce:   docuswiftPortal.nonce,
                    type:    form.querySelector('#support-type').value,
                    subject: form.querySelector('#support-subject').value.trim(),
                    message: form.querySelector('#support-message').value.trim(),
                });

                const res  = await fetch(docuswiftPortal.ajax_url, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body:    params.toString(),
                });
                const data = await res.json();

                if (data.success) {
                    showNotice(data.data.message, 'success');
                    form.reset();
                } else {
                    showNotice(data.data?.message || 'An error occurred. Please try again.', 'error');
                }
            } catch (err) {
                console.error(err);
                showNotice('Network error. Please try again.', 'error');
            } finally {
                submitBtn.disabled    = false;
                submitBtn.textContent = submitBtn._orig || 'Send Message';
            }
        });
    }

    initForgotPasswordForm() {
        const form = document.getElementById('custom-forgot-password-form');
        if (!form) return;

        const msgBox  = document.getElementById('forgot-password-message');
        const submitBtn = form.querySelector('.sign-in-btn');

        const showMsg = (text, type) => {
            msgBox.querySelector('p').textContent = text;
            msgBox.className = `form-notice form-notice--${type}`;
            msgBox.style.display = 'block';
        };

        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            const email = form.querySelector('#user_login').value.trim();

            submitBtn.classList.add('loading');
            submitBtn.disabled = true;
            msgBox.style.display = 'none';

            try {
                const params = new URLSearchParams({
                    action: 'docuswift_forgot_password',
                    email,
                    nonce: docuswiftPortal.nonce,
                });

                const res  = await fetch(docuswiftPortal.ajax_url, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body:    params.toString(),
                });
                const data = await res.json();

                if (data.success) {
                    showMsg(data.data.message, 'success');
                    form.style.display = 'none';
                } else {
                    showMsg(data.data?.message || 'An error occurred. Please try again.', 'error');
                }
            } catch (err) {
                console.error(err);
                showMsg('Network error. Please try again.', 'error');
            } finally {
                submitBtn.classList.remove('loading');
                submitBtn.disabled = false;
            }
        });
    }

    /**
     * Initialize utilities
     */
    initUtilities() {
        // Scroll chat histories to bottom
        ChatScroller.init('.chat_history');
    }
    /**
     * Get component by name
     */
    getComponent(name) {
        return this.components[name];
    }

    loadPageSpecificScripts() {
        // Case Manager page
        if (document.body.classList.contains('page-template-page-case-managers')) {
            this.initCaseManagerPage();
        }

        // Facility modal: init wherever it's present (Facilities page + Requests page).
        // The Facility component only touches the table via optional chaining so it
        // works fine when no `.facilities_table` exists on the page.
        if (document.getElementById('ds_facility_modal')) {
            this.initFacilityPage();
        }
    }

    initCaseManagerPage() {
        const caseManager = new CaseManager('#add_case_manager_modal');
        caseManager.init();
    }

    initFacilityPage() {
        const facility = new Facility('#ds_facility_modal', {
            facilitiesTable: this.components.facilitiesTable,
        });
        
        facility.init();
        this.components.facility = facility;
    }

    /**
     * Destroy all components
     */
    destroy() {
        Object.keys(this.components).forEach(key => {
            const component = this.components[key];
            if (component && typeof component.destroy === 'function') {
                component.destroy();
            }
        });
        this.components = {};
    }
}

/**
 * Initialize application when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
    // Create and initialize the application
    const app = new DocuswiftPortalApp();
    app.init();

    // Make app globally accessible for debugging
    window.DocuswiftPortal = app;

    // Make eventBus globally accessible
    window.DocuswiftEventBus = eventBus;
});

// Export for potential use as module
export default DocuswiftPortalApp;

// Smooth scroll for anchor links — without adding # to the URL
document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;
    const target = document.querySelector(hash);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});


